import React from 'react'
import { Link } from 'react-router-dom'
import { ExclamationTriangleIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { CenyZOfertWykres, CenaKalkulator } from './CenyZOfert'

/* ===== KeyFact 3,68 kW ===== */
export const KeyFact = () => (
  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-5 rounded-xl border border-gray-200 bg-gray-50 p-5 sm:p-6">
    <div className="flex-shrink-0 flex flex-col items-center justify-center rounded-lg bg-white border border-gray-200 px-6 py-4 text-center">
      <span className="text-3xl sm:text-4xl font-extrabold text-blue-600 leading-none">3,68 kW</span>
      <span className="mt-1.5 text-[11px] uppercase tracking-wide text-gray-400">granica jednofazowa</span>
    </div>
    <div className="text-sm text-gray-600 leading-6">
      <p className="font-semibold text-gray-800 mb-1">Kluczowa wartość przy doborze falownika</p>
      <p>Instalacje o mocy zainstalowanej powyżej 3,68 kW są w polskich warunkach przyłączane trójfazowo. Poniżej tej wartości można rozważyć falownik 1-fazowy, jeśli pozwalają na to wymagania OSD.</p>
    </div>
  </div>
)

/* ===== Tabela porównawcza ===== */
const CMP_ROWS = [
  ['Typowe zastosowanie', 'Małe instalacje', 'Większe instalacje'],
  ['Przyłącze', 'Jednofazowe lub trójfazowe', 'Trójfazowe'],
  ['Wprowadzanie mocy', 'Jedna faza', 'Trzy fazy'],
  ['Cena urządzenia', 'Często niższa', 'Często wyższa'],
  ['Liczba MPPT', 'Zależy od modelu, najczęściej 1', 'Zależy od modelu, najczęściej od 2'],
  ['Instalacja powyżej 3,68 kW', 'Nie', 'Tak'],
]
export const ComparisonTable = () => (
  <figure>
    <div className="overflow-x-auto border border-gray-200 rounded-xl">
      <table className="w-full min-w-[520px] text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 text-left">
            <th className="px-4 py-3 font-semibold text-gray-500 border-b border-gray-200">Kryterium</th>
            <th className="px-4 py-3 font-semibold text-gray-700 border-b border-l border-gray-200">Falownik 1-fazowy</th>
            <th className="px-4 py-3 font-semibold text-gray-700 border-b border-l border-gray-200">Falownik 3-fazowy</th>
          </tr>
        </thead>
        <tbody>
          {CMP_ROWS.map((r) => (
            <tr key={r[0]} className="odd:bg-white even:bg-gray-50/40">
              <th scope="row" className="px-4 py-3 text-left font-medium text-gray-600 border-t border-gray-100 align-top">{r[0]}</th>
              <td className="px-4 py-3 text-gray-700 border-t border-l border-gray-100 align-top">{r[1]}</td>
              <td className="px-4 py-3 text-gray-700 border-t border-l border-gray-100 align-top">{r[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <figcaption className="mt-2 text-xs text-gray-400">Zestawienie ogólne — szczegóły zależą od konkretnego modelu falownika.</figcaption>
  </figure>
)

/* ===== Schemat rozkładu mocy na fazy ===== */
const PhaseRow = ({ label, kw, pct, active }) => (
  <div className="flex items-center gap-3">
    <span className="w-7 text-xs font-semibold text-gray-500">{label}</span>
    <div className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
      <div className={`h-full rounded-full ${active ? 'bg-blue-500' : 'bg-gray-200'}`} style={{ width: `${pct}%` }} />
    </div>
    <span className="w-14 text-right text-xs text-gray-600 tabular-nums">{kw}</span>
  </div>
)
export const PhaseSchematic = () => (
  <figure className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
    <div className="grid gap-6 sm:grid-cols-2">
      <div>
        <div className="inline-flex items-center rounded-md bg-gray-50 border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 mb-4">Falownik 1-fazowy · 3,6 kW</div>
        <div className="space-y-2.5">
          <PhaseRow label="L1" kw="3,6 kW" pct={100} active />
          <PhaseRow label="L2" kw="0 kW" pct={0} />
          <PhaseRow label="L3" kw="0 kW" pct={0} />
        </div>
        <p className="mt-3 text-xs text-gray-500">Cała moc wprowadzana na jedną fazę.</p>
      </div>
      <div className="sm:border-l sm:border-gray-100 sm:pl-6">
        <div className="inline-flex items-center rounded-md bg-gray-50 border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 mb-4">Falownik 3-fazowy · 9 kW</div>
        <div className="space-y-2.5">
          <PhaseRow label="L1" kw="~3 kW" pct={83} active />
          <PhaseRow label="L2" kw="~3 kW" pct={83} active />
          <PhaseRow label="L3" kw="~3 kW" pct={83} active />
        </div>
        <p className="mt-3 text-xs text-gray-500">Moc rozłożona równo na trzy fazy.</p>
      </div>
    </div>
    <figcaption className="mt-4 text-xs text-gray-400">Uproszczony rozkład mocy wyjściowej na fazy — nie jest to schemat instalacji elektrycznej.</figcaption>
  </figure>
)

/* ===== Schemat MPPT ===== */
const SingleRoof = () => (
  <svg viewBox="0 0 100 60" className="w-full h-16" role="img" aria-label="Jednolita połać dachu">
    <polygon points="12,48 50,18 88,48" fill="#dbeafe" stroke="#93c5fd" strokeWidth="2" />
  </svg>
)
const EWRoof = () => (
  <svg viewBox="0 0 100 60" className="w-full h-16" role="img" aria-label="Dach wschód-zachód">
    <polygon points="12,48 50,18 50,48" fill="#dbeafe" stroke="#93c5fd" strokeWidth="2" />
    <polygon points="50,48 50,18 88,48" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="2" />
  </svg>
)
const MultiRoof = () => (
  <svg viewBox="0 0 100 60" className="w-full h-16" role="img" aria-label="Kilka pól modułów">
    <rect x="12" y="34" width="20" height="14" rx="2" fill="#dbeafe" stroke="#93c5fd" strokeWidth="2" />
    <rect x="40" y="26" width="20" height="22" rx="2" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="2" />
    <rect x="68" y="38" width="20" height="10" rx="2" fill="#f0f9ff" stroke="#bae6fd" strokeWidth="2" />
  </svg>
)
const MpptCard = ({ svg, mppt, label, desc }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 flex flex-col">
    <div className="h-16 flex items-center justify-center">{svg}</div>
    <div className="mt-2 text-sm font-bold text-blue-600">{mppt}</div>
    <div className="text-sm font-semibold text-gray-800">{label}</div>
    <p className="mt-1 text-xs text-gray-500 leading-5">{desc}</p>
  </div>
)
export const MpptSchematic = () => (
  <figure className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
    <div className="grid gap-4 sm:grid-cols-3">
      <MpptCard svg={<SingleRoof />} mppt="1 MPPT" label="Jednolita połać" desc="Jeden kierunek i nachylenie mogą poprawnie współpracować z jednym trackerem." />
      <MpptCard svg={<EWRoof />} mppt="2 MPPT" label="Dach wschód–zachód" desc="Dwie połacie o różnej ekspozycji zwykle wymagają dwóch niezależnych MPPT." />
      <MpptCard svg={<MultiRoof />} mppt="≥ 3 MPPT" label="Kilka różnych pól" desc="Wiele różniących się pól modułów może wymagać większej liczby trackerów lub innego podziału." />
    </div>
    <div className="mt-4 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800">
      <strong className="font-semibold">Zapamiętaj:</strong> liczba MPPT nie wynika bezpośrednio z liczby faz falownika.
    </div>
  </figure>
)

/* ===== Scenariusze doboru ===== */
const TAG_STYLE = {
  '1-fazowy': 'bg-slate-100 text-slate-600',
  '3-fazowy': 'bg-blue-100 text-blue-700',
  'porównaj oba': 'bg-indigo-50 text-indigo-600',
}
const SCENARIOS = [
  { cond: '3 kWp · przyłącze 1-fazowe', tag: '1-fazowy', reason: 'Moc poniżej 3,68 kW, a układ przyłącza nie pozwala rozdzielić mocy na trzy fazy.' },
  { cond: '3,5 kWp · przyłącze 3-fazowe', tag: 'porównaj oba', reason: '1-fazowy bywa tańszy, ale wprowadza całą moc na jedną fazę. 3-fazowy ogranicza asymetrię — sprawdź pracę przy małym generatorze i zakres MPPT.' },
  { cond: '6 kWp · przyłącze 3-fazowe', tag: '3-fazowy', reason: 'Moc przekracza 3,68 kW, więc instalacja powinna oddawać energię trójfazowo.' },
  { cond: '9 kWp · kilka połaci dachowych', tag: '3-fazowy', reason: 'Punkt wyjścia to falownik 3-fazowy z uwagi na moc; potem dobierz liczbę MPPT i zakres napięć. Liczba połaci nie przesądza o kilku falownikach.' },
]
export const ScenarioCards = () => (
  <div className="grid gap-4 sm:grid-cols-2">
    {SCENARIOS.map((s) => (
      <div key={s.cond} className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-2">
          <p className="text-sm font-semibold text-gray-800">{s.cond}</p>
          <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${TAG_STYLE[s.tag]}`}>{s.tag}</span>
        </div>
        <p className="text-sm text-gray-600 leading-6">{s.reason}</p>
      </div>
    ))}
  </div>
)

/* ===== Na co uważać ===== */
const PITFALLS = [
  'Falownik 1-fazowy przy instalacji przekraczającej 3,68 kW.',
  'Wybór wyłącznie na podstawie ceny.',
  'Mylenie liczby faz z liczbą MPPT.',
  'Brak sprawdzenia wymagań właściwego OSD.',
  'Brak analizy napięcia i prądów stringów.',
  'Założenie, że falownik 3-fazowy zawsze rozwiązuje problem wysokiego napięcia.',
]
export const Pitfalls = () => (
  <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-5 sm:p-6">
    <div className="flex items-center gap-2 mb-4">
      <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
      <p className="text-sm font-semibold text-gray-800">Najczęstsze błędy przy doborze</p>
    </div>
    <ul className="grid gap-3 sm:grid-cols-2">
      {PITFALLS.map((p) => (
        <li key={p} className="flex gap-2 text-sm text-gray-700 leading-6">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
          {p}
        </li>
      ))}
    </ul>
  </div>
)

/* ===== Filtry VoltValue (mockup) ===== */
const VV_FILTERS = ['Liczba faz', 'Moc', 'Liczba MPPT', 'Typ falownika', 'Producent', 'Cena', 'Dostępność']
export const VoltValueFilters = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
    <p className="text-sm font-semibold text-gray-800 mb-3">Filtry falowników w VoltValue</p>
    <div className="flex flex-wrap gap-2 mb-4">
      {VV_FILTERS.map((f) => (
        <span key={f} className="rounded-md bg-gray-50 border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600">{f}</span>
      ))}
    </div>
    <p className="text-sm text-gray-600 leading-6 mb-4">
      Najpierw zawęź urządzenia po liczbie faz i mocy. Następnie porównaj MPPT, parametry wejściowe, gwarancję i cenę.
    </p>
    <Link to="/inwertery" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline">
      Przejdź do falowników <ChevronRightIcon className="w-4 h-4" />
    </Link>
  </div>
)

/* ===== Reużywalny callout ostrzegawczy (spokojny) ===== */
export const WarningCallout = ({ title, children }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50/50 border-l-4 border-l-amber-300 p-4 sm:p-5">
    <div className="flex items-start gap-3">
      <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-gray-800 leading-6">{title}</p>
        <p className="mt-1 text-sm text-gray-600 leading-6">{children}</p>
      </div>
    </div>
  </div>
)

/* ===== Art. 2: schemat fizyka / pomiar / rozliczenie ===== */
const FlowStep = ({ n, title, children }) => (
  <div className="relative rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
    <div className="flex items-center gap-2 mb-2">
      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">{n}</span>
      <p className="text-sm font-semibold text-gray-800">{title}</p>
    </div>
    <div className="text-sm text-gray-600 leading-6">{children}</div>
  </div>
)
export const PhaseFlowSchematic = () => (
  <figure className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:p-6">
    <div className="grid gap-3 sm:gap-4 sm:grid-cols-3">
      <FlowStep n="1" title="Fizyczny przepływ">
        Falownik 1-fazowy → <strong className="font-semibold text-gray-800">L1</strong>.
        <span className="mt-2 block rounded-md bg-white border border-gray-200 px-3 py-2 text-xs">
          L1: produkcja i lokalne zużycie<br />L2: pobór odbiorników<br />L3: pobór odbiorników
        </span>
      </FlowStep>
      <FlowStep n="2" title="Pomiar">
        Układ pomiarowy rejestruje:
        <span className="mt-2 block rounded-md bg-white border border-gray-200 px-3 py-2 text-xs">
          eksport na L1<br />import na L2<br />import na L3
        </span>
      </FlowStep>
      <FlowStep n="3" title="Rozliczenie">
        Do rozliczenia trafia sumaryczny wynik po bilansowaniu faz w danej godzinie.
        <span className="mt-2 block rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800">
          Σ faz → wynik godzinowy
        </span>
      </FlowStep>
    </div>
    <figcaption className="mt-4 text-xs text-gray-400">Trzy różne warstwy: fizyczny przepływ, rejestracja pomiarowa i wynik rozliczeniowy — to nie jest schemat instalacji elektrycznej.</figcaption>
  </figure>
)

/* ===== Art. 2: przykład liczbowy (produkcja / pobór / wynik) ===== */
export const NumericExample = () => (
  <figure className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
    <div className="grid gap-4 sm:grid-cols-3">
      <div className="rounded-lg border border-gray-200 p-4">
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Produkcja</p>
        <p className="text-sm text-gray-700">L1: <strong className="font-semibold text-blue-600">2,7 kWh</strong></p>
      </div>
      <div className="rounded-lg border border-gray-200 p-4">
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Pobór</p>
        <p className="text-sm text-gray-700 leading-6">L1: 0,5 kWh<br />L2: 0,8 kWh<br />L3: 0,6 kWh</p>
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
        <p className="text-xs uppercase tracking-wide text-emerald-500 mb-2">Wynik po bilansowaniu</p>
        <p className="text-sm text-gray-700"><strong className="font-semibold text-emerald-600">0,3 kWh</strong> nadwyżki</p>
      </div>
    </div>
    <figcaption className="mt-3 text-xs text-gray-400">Instalacja 3,6 kW na L1, godzina 12:00–13:00.</figcaption>
  </figure>
)

/* ===== Art. 2: dwa błędne wnioski o bilansowaniu ===== */
export const BalancingWarnings = () => (
  <div className="space-y-3">
    <WarningCallout title={'„Skoro fazy się bilansują, to falownik 1-fazowy działa praktycznie jak 3-fazowy”'}>
      Nie działa. Bilansowanie dotyczy rozliczenia energii, a nie sposobu obciążania sieci. Falownik 1-fazowy
      nadal oddaje moc jedną fazą, powoduje większą asymetrię i może lokalnie podnosić napięcie na tej fazie.
    </WarningCallout>
    <WarningCallout title={'„Jeżeli bilansowanie działa, to czas produkcji i poboru nie ma znaczenia”'}>
      Ma znaczenie — rozliczenie liczone jest z energii sumarycznie zbilansowanej w każdej godzinie. Nadwyżka
      w jednej godzinie i pobór w kolejnej to nie to samo co jednoczesny pobór i eksport w tej samej godzinie.
    </WarningCallout>
  </div>
)

/* ===== Art. 2: 1 falownik 3-fazowy vs kilka 1-fazowych ===== */
const MI_ROWS = [
  ['Liczba urządzeń', '1', '2 lub 3'],
  ['Montaż i okablowanie', 'prostsze', 'bardziej złożone'],
  ['Monitoring', 'zwykle jeden system', 'zależny od producenta i integracji'],
  ['Wspólne sterowanie', 'z natury prostsze', 'wymaga weryfikacji rozwiązania'],
  ['Serwis', 'jedno urządzenie', 'więcej punktów potencjalnej awarii'],
  ['Elastyczność etapowej rozbudowy', 'mniejsza', 'czasem większa'],
  ['Typowy sens w mikroinstalacji domowej', 'wysoki', 'ograniczony do wybranych przypadków'],
]
export const MultiInverterTable = () => (
  <figure>
    <div className="overflow-x-auto border border-gray-200 rounded-xl">
      <table className="w-full min-w-[560px] text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 text-left">
            <th className="px-4 py-3 font-semibold text-gray-500 border-b border-gray-200">Kryterium</th>
            <th className="px-4 py-3 font-semibold text-gray-700 border-b border-l border-gray-200">1 falownik 3-fazowy</th>
            <th className="px-4 py-3 font-semibold text-gray-700 border-b border-l border-gray-200">kilka falowników 1-fazowych</th>
          </tr>
        </thead>
        <tbody>
          {MI_ROWS.map((r) => (
            <tr key={r[0]} className="odd:bg-white even:bg-gray-50/40">
              <th scope="row" className="px-4 py-3 text-left font-medium text-gray-600 border-t border-gray-100 align-top">{r[0]}</th>
              <td className="px-4 py-3 text-gray-700 border-t border-l border-gray-100 align-top">{r[1]}</td>
              <td className="px-4 py-3 text-gray-700 border-t border-l border-gray-100 align-top">{r[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </figure>
)

/* ===== Art. 2: warianty A/B ===== */
const PhasePins = ({ items }) => (
  <div className="space-y-1.5">
    {items.map((it) => (
      <div key={it} className="flex items-center gap-2 text-xs text-gray-600">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> {it}
      </div>
    ))}
  </div>
)
export const VariantSchematic = () => (
  <div className="grid gap-4 sm:grid-cols-2">
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-sm font-semibold text-gray-800 mb-3">Wariant A: 1 falownik 3-fazowy</p>
      <div className="inline-flex items-center rounded-md bg-blue-50 border border-blue-100 px-3 py-1.5 text-sm font-medium text-blue-700 mb-3">Falownik 3-fazowy</div>
      <PhasePins items={['→ L1', '→ L2', '→ L3']} />
    </div>
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-sm font-semibold text-gray-800 mb-3">Wariant B: kilka falowników 1-fazowych</p>
      <PhasePins items={['Falownik 1 → L1', 'Falownik 2 → L2', 'Falownik 3 → L3']} />
      <p className="mt-3 text-xs text-gray-400">Schemat koncepcyjny, nie wykonawczy.</p>
    </div>
  </div>
)

/* ===== Art. 2: integrator / wspólne sterowanie ===== */
export const IntegratorSchematic = () => (
  <figure className="rounded-xl border border-gray-200 bg-gray-50/60 p-5 sm:p-6">
    <div className="flex flex-col items-center gap-2 text-center">
      <div className="flex flex-wrap justify-center gap-2">
        {['Falownik 1', 'Falownik 2', 'Falownik 3'].map((f) => (
          <span key={f} className="rounded-md bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700">{f}</span>
        ))}
      </div>
      <span className="text-gray-300 text-lg leading-none">↓</span>
      <span className="rounded-md bg-blue-50 border border-blue-100 px-4 py-2 text-sm font-medium text-blue-700">kontroler / integrator / urządzenie nadrzędne</span>
      <span className="text-gray-300 text-lg leading-none">↓</span>
      <span className="rounded-md bg-white border border-gray-200 px-4 py-2 text-sm text-gray-700">wspólne sterowanie mikroinstalacją</span>
    </div>
    <figcaption className="mt-4 text-xs text-gray-500 text-center max-w-xl mx-auto">
      Przy więcej niż jednym falowniku trzeba sprawdzić, w jaki sposób system zapewnia wspólne i jednoczesne
      sterowanie całą mikroinstalacją. Rozwiązanie zależy od producenta i wymagań właściwego OSD.
    </figcaption>
  </figure>
)

export const buildComponentMap = () => ({
  // Artykuł 1
  'comparison-table': <ComparisonTable />,
  'key-fact-368': <KeyFact />,
  'phase-schematic': <PhaseSchematic />,
  'mppt-schematic': <MpptSchematic />,
  'scenario-cards': <ScenarioCards />,
  'pitfalls': <Pitfalls />,
  'voltvalue-filters': <VoltValueFilters />,
  // Artykuł 2
  'phase-flow': <PhaseFlowSchematic />,
  'numeric-example': <NumericExample />,
  'balancing-warnings': <BalancingWarnings />,
  'multi-inverter-table': <MultiInverterTable />,
  'variant-schematic': <VariantSchematic />,
  'integrator-schematic': <IntegratorSchematic />,
  // Fotowoltaika w 2027: ceny z ofert
  'ceny-z-ofert-wykres': <CenyZOfertWykres />,
  'kalkulator-ceny-z-ofert': <CenaKalkulator />,
})
